import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Scissors, GripVertical, Trash2, Plus, Film, Loader2, Download, ArrowUp, ArrowDown, Play } from 'lucide-react';
import { toast } from 'sonner';

export default function VideoEditorPage() {
  const [clips, setClips] = useState([]);
  const [availableClips, setAvailableClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [ratio, setRatio] = useState('16:9');
  const [trimTarget, setTrimTarget] = useState(null);
  const [trimStart, setTrimStart] = useState('0');
  const [trimEnd, setTrimEnd] = useState('');
  const [trimming, setTrimming] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const streamUrl = (url) => url ? `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}` : '';

  useEffect(() => {
    api.get('/content/library?content_type=video&limit=100')
      .then(r => { setAvailableClips(r.data.items.filter(i => i.url)); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const addClip = (item) => {
    if (clips.find(c => c.id === item.id)) { toast.error('Bu klip zaten eklendi'); return; }
    setClips(prev => [...prev, item]);
  };

  const removeClip = (id) => setClips(prev => prev.filter(c => c.id !== id));
  const moveClip = (idx, dir) => {
    const arr = [...clips];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setClips(arr);
  };

  const handleMerge = async () => {
    if (clips.length < 2) { toast.error('En az 2 klip seçin'); return; }
    setMerging(true); setMergeResult(null);
    try {
      const res = await api.post('/generate/video/merge', {
        clip_urls: clips.map(c => c.url), aspect_ratio: ratio,
      });
      setMergeResult(res.data);
      toast.success('Klipler birleştirildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Birleştirme başarısız');
    }
    setMerging(false);
  };

  const handleTrim = async () => {
    if (!trimTarget?.url) return;
    setTrimming(true);
    try {
      const res = await api.post('/generate/video/trim', {
        video_url: trimTarget.url, start_time: parseFloat(trimStart) || 0, end_time: parseFloat(trimEnd) || 0,
      });
      setMergeResult(res.data);
      toast.success('Video kesildi');
      setTrimTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kesme başarısız');
    }
    setTrimming(false);
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Düzenleyici yükleniyor...</div>;

  return (
    <div data-testid="video-editor-page" className="animate-fade-in space-y-6">
      <div className="pb-6 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Video Düzenleyici</h1>
        <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Klipleri sıralayın, kesin ve birleştirin</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Clip Library */}
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm text-white">Mevcut Klipler</CardTitle></CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto space-y-2">
            {availableClips.length === 0 && <p className="text-xs text-[#737373] font-ibm py-4">Video klip bulunamadı</p>}
            {availableClips.map(item => (
              <div key={item.id} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded border border-[#1A1A1A] hover:border-[#404040] transition-colors">
                <div className="w-16 h-10 bg-[#0A0A0A] rounded overflow-hidden shrink-0">
                  <video src={streamUrl(item.url)} className="w-full h-full object-cover" muted preload="metadata" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-ibm truncate">{item.title}</p>
                  <p className="text-xs text-[#525252] font-jetbrains">{new Date(item.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => addClip(item)} className="h-7 w-7 p-0 text-[#A3A3A3] hover:text-[#FACC15]" data-testid={`add-clip-${item.id}`}><Plus size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => { setTrimTarget(item); setTrimEnd(''); }} className="h-7 w-7 p-0 text-[#A3A3A3] hover:text-blue-400"><Scissors size={14} /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Center: Timeline */}
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit text-sm text-white">Zaman Çizelgesi ({clips.length} klip)</CardTitle>
              <Select value={ratio} onValueChange={setRatio}>
                <SelectTrigger className="w-32 h-7 text-xs bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="16:9">Yatay (16:9)</SelectItem>
                  <SelectItem value="9:16">Dikey (9:16)</SelectItem>
                  <SelectItem value="1:1">Kare (1:1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 min-h-[300px]">
            {clips.length === 0 && (
              <div className="text-center py-12"><GripVertical size={32} className="mx-auto text-[#262626] mb-2" /><p className="text-xs text-[#737373] font-ibm">Soldaki listeden klip ekleyin</p></div>
            )}
            {clips.map((clip, idx) => (
              <div key={clip.id} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded border border-[#262626]" data-testid={`timeline-clip-${idx}`}>
                <span className="text-xs text-[#525252] font-jetbrains w-5">{idx + 1}</span>
                <div className="w-12 h-8 rounded overflow-hidden shrink-0">
                  <video src={streamUrl(clip.url)} className="w-full h-full object-cover" muted preload="metadata" />
                </div>
                <p className="text-xs text-white font-ibm flex-1 truncate">{clip.title}</p>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="sm" onClick={() => moveClip(idx, -1)} disabled={idx === 0} className="h-6 w-6 p-0 text-[#525252] hover:text-white"><ArrowUp size={12} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => moveClip(idx, 1)} disabled={idx === clips.length - 1} className="h-6 w-6 p-0 text-[#525252] hover:text-white"><ArrowDown size={12} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => removeClip(clip.id)} className="h-6 w-6 p-0 text-[#525252] hover:text-red-400"><Trash2 size={12} /></Button>
                </div>
              </div>
            ))}
            {clips.length >= 2 && (
              <Button onClick={handleMerge} disabled={merging} data-testid="merge-clips-btn" className="w-full mt-4 bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] h-10">
                {merging ? <><Loader2 size={14} className="mr-2 animate-spin" />Birleştiriliyor...</> : <><Film size={14} className="mr-2" />Klipleri Birleştir</>}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Right: Result + Trim */}
        <div className="space-y-4">
          {/* Trim */}
          {trimTarget && (
            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm text-white flex items-center gap-2"><Scissors size={14} className="text-[#FACC15]" /> Video Kes</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <video controls className="w-full rounded border border-[#262626] max-h-[150px]" src={streamUrl(trimTarget.url)} />
                <p className="text-xs text-[#A3A3A3] font-ibm truncate">{trimTarget.title}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[#525252] font-ibm text-xs">Başlangıç (sn)</Label>
                    <Input type="number" step="0.1" value={trimStart} onChange={e => setTrimStart(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs mt-0.5" data-testid="trim-start" />
                  </div>
                  <div>
                    <Label className="text-[#525252] font-ibm text-xs">Bitiş (sn)</Label>
                    <Input type="number" step="0.1" value={trimEnd} onChange={e => setTrimEnd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs mt-0.5" placeholder="Boş = son" data-testid="trim-end" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleTrim} disabled={trimming} data-testid="trim-btn" className="flex-1 bg-white text-black hover:bg-[#E5E5E5] h-8 text-xs">
                    {trimming ? <Loader2 size={12} className="animate-spin" /> : <><Scissors size={12} className="mr-1" />Kes</>}
                  </Button>
                  <Button variant="outline" onClick={() => setTrimTarget(null)} className="border-[#262626] text-[#A3A3A3] h-8 text-xs">İptal</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Merge Result */}
          {mergeResult && (
            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm text-white">Sonuç</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <video controls className="w-full rounded-lg border border-[#262626]" data-testid="editor-result-video">
                  <source src={streamUrl(mergeResult.url)} type="video/mp4" />
                </video>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {mergeResult.duration > 0 && <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs">{Math.round(mergeResult.duration)}s</Badge>}
                    {mergeResult.clip_count && <Badge variant="outline" className="text-blue-400 border-blue-500/20 text-xs">{mergeResult.clip_count} klip</Badge>}
                  </div>
                  <a href={`${backendUrl}${mergeResult.url}`} download className="text-xs text-[#FACC15] hover:text-white font-ibm flex items-center gap-1"><Download size={12} /> İndir</a>
                </div>
              </CardContent>
            </Card>
          )}

          {!trimTarget && !mergeResult && (
            <Card className="bg-[#141414] border-[#262626]">
              <CardContent className="p-8 text-center">
                <Scissors size={32} className="mx-auto text-[#262626] mb-3" />
                <p className="text-xs text-[#737373] font-ibm">Klipleri sıralayıp birleştirin veya bir klibi kesin</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
